import { createServerFn } from "@tanstack/react-start"
import { requireAuth, requireScopes, rateLimitFunction } from "~/lib/middleware"
import { ApiResponse, Organization, organizationSchema, organizationStatusSchema } from "~/lib/schemas"
import z from "zod"
import { LogtoScopes } from "~/lib/logto/logto"

export const filterOrgsSchema = z.object({
  name: z.string().optional(),
  search: z.string().optional(),
  status: organizationStatusSchema.optional(),
  limit: z.number().min(1).max(1000).optional(),
  offset: z.number().min(0).optional(),
  sortBy: z.enum(["name", "created_at", "updated_at"]).optional(),
  sortOrder: z.enum(["asc", "desc"]).optional().default("asc"),
})

export type FilterOrgsRequest = z.infer<typeof filterOrgsSchema>

export async function filterOrgsImpl(request: FilterOrgsRequest): Promise<Organization[]> {
  const { name, status, limit, offset, sortBy, sortOrder } = request
  
  // Use real API
  const API_MANAGEMENT_BASE_URL = process.env.API_MANAGEMENT_BASE_URL || 'http://localhost:8000'
  const BFF_API_KEY = process.env.API_MANAGEMENT_API_KEY
  
  if (!BFF_API_KEY) {
    throw new Error('API_MANAGEMENT_API_KEY environment variable is required')
  }
  
  const params = new URLSearchParams()
  if (name) params.append('name', name)
  if (status) params.append('status', status.toLowerCase())
  if (limit) params.append('limit', limit.toString())
  if (offset !== undefined) params.append('offset', offset.toString())
  if (sortBy) params.append('sortBy', sortBy)
  if (sortOrder) params.append('sortOrder', sortOrder)

  const response = await fetch(`${API_MANAGEMENT_BASE_URL}/api/v4/admin/organizations/list?${params.toString()}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${BFF_API_KEY}`,
      'Content-Type': 'application/json',
    },
  })
  
  if (!response.ok) {
    const errorData = await response.text()
    let errorDataJson
    try {
      errorDataJson = JSON.parse(errorData)
    } catch {
      errorDataJson = { detail: errorData }
    }
    
    throw new Error(errorDataJson.detail || `HTTP ${response.status}: ${response.statusText}`)
  }
  
  const result = await response.json() as ApiResponse<Organization[]>
  let organizations = z.array(organizationSchema).parse(result.data ?? [])
  
  // Apply pagination (client-side fallback if API doesn't handle it)
  if (offset !== undefined || limit !== undefined) {
    const start = offset || 0
    const end = limit ? start + limit : undefined
    organizations = organizations.slice(start, end)
  }
  
  return organizations
}

export const filterOrgs = createServerFn({
  method: 'GET',
})
  .middleware([requireAuth, rateLimitFunction, requireScopes(LogtoScopes.AdminOrgRead)])
  .validator(filterOrgsSchema)
  .handler(async (ctx) => {
    return filterOrgsImpl(ctx.data)
  })


